<template>
  <div class="card border-0 shadow-sm p-3">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div class="d-flex align-items-center">
        <i class="bi bi-people me-2 text-primary"></i>
        <strong>Team Members</strong>
        <span class="badge bg-secondary ms-2">{{ childUsers.length }} / {{ effectiveMaxUsers }}</span>
      </div>
      <button v-if="canAddUser" class="btn btn-sm btn-outline-primary" @click="showAddForm = !showAddForm" :disabled="isLoading">
        <i class="bi bi-plus-lg me-1"></i>Add User
      </button>
    </div>

    <!-- Error -->
    <div v-if="errorMsg" class="alert alert-danger alert-dismissible py-2 mb-2">
      {{errorMsg}}
      <button type="button" class="btn-close btn-close-sm" @click="errorMsg=null"></button>
    </div>

    <!-- Add user form -->
    <div v-if="showAddForm" class="border rounded p-3 mb-3 bg-light">
      <!-- Mode toggle -->
      <div class="btn-group btn-group-sm mb-3 w-100" role="group">
        <button type="button" class="btn" :class="addMode === 'user' ? 'btn-primary' : 'btn-outline-primary'" @click="addMode = 'user'"><i class="bi bi-person me-1"></i>Team Member</button>
        <button type="button" class="btn" :class="addMode === 'apikey' ? 'btn-primary' : 'btn-outline-primary'" @click="addMode = 'apikey'"><i class="bi bi-key me-1"></i>API Key</button>
      </div>
      <div class="row g-2 align-items-end">
        <!-- API Key mode: just a label -->
        <template v-if="addMode === 'apikey'">
          <div class="col-12 col-md-8">
            <label class="form-label form-label-sm mb-1">Label</label>
            <input type="text" class="form-control form-control-sm" v-model.trim="newLabel" placeholder="e.g. Production, Staging, Mobile App" @keyup.enter="createUser" />
          </div>
          <div class="col-12 col-md-4 d-flex gap-2">
            <button class="btn btn-sm btn-primary flex-grow-1" @click="createUser" :disabled="!newLabel || isBusy">
              <span v-if="isBusy" class="spinner-border spinner-border-sm me-1"></span>Create
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="cancelAdd">Cancel</button>
          </div>
          <div class="form-text text-muted mt-1" style="font-size:0.75rem;">Creates an API key with a friendly label. No login access — API-only.</div>
        </template>
        <!-- Team Member mode: email + password -->
        <template v-else>
          <div class="col-12 col-md-5">
            <label class="form-label form-label-sm mb-1">Email</label>
            <input type="email" class="form-control form-control-sm" v-model.trim="newEmail" placeholder="e.g. colleague@company.com" @keyup.enter="createUser" />
          </div>
          <div class="col-12 col-md-4">
            <label class="form-label form-label-sm mb-1">Password <span class="text-muted">(optional)</span></label>
            <input type="text" class="form-control form-control-sm" v-model="newPassword" placeholder="Auto-generated if blank" @keyup.enter="createUser" />
          </div>
          <div class="col-12 col-md-3 d-flex gap-2">
            <button class="btn btn-sm btn-primary flex-grow-1" @click="createUser" :disabled="!newEmail || isBusy">
              <span v-if="isBusy" class="spinner-border spinner-border-sm me-1"></span>Create
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="cancelAdd">Cancel</button>
          </div>
          <div class="form-text text-muted mt-1" style="font-size:0.75rem;">Creates a team member with their own login and API key.</div>
        </template>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="d-flex align-items-center text-muted py-3">
      <div class="spinner-border spinner-border-sm me-2"></div>Loading team members...
    </div>

    <!-- Empty state -->
    <div v-else-if="!childUsers.length" class="text-muted py-2">
      No team members yet.
    </div>

    <!-- User list -->
    <div v-else class="table-responsive">
      <table class="table table-sm table-hover mb-0 align-middle">
        <thead>
          <tr class="text-muted small">
            <th class="fw-normal">Name</th>
            <th class="fw-normal">Type</th>
            <th class="fw-normal">API Key</th>
            <th class="fw-normal text-center">Admin</th>
            <th class="fw-normal text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in childUsers" :key="user.id">
            <td>
              <div>{{ displayName(user) }}</div>
              <div v-if="user.email && user.isApiKey !== 'true'" class="text-muted small">{{ user.email }}</div>
            </td>
            <td>
              <span v-if="user.isApiKey === 'true'" class="badge bg-info bg-opacity-10 text-info"><i class="bi bi-key me-1"></i>API Key</span>
              <span v-else class="badge bg-primary bg-opacity-10 text-primary"><i class="bi bi-person me-1"></i>Member</span>
            </td>
            <td class="text-nowrap" style="cursor:pointer;" @click="copyApiKey(user.apiKey, user.id)" title="Click to copy">
              <code class="small">{{ copiedKey === user.id ? 'Copied!' : user.apiKey }}</code>
            </td>
            <td class="text-center">
              <i v-if="user.isClientAdmin === 'true'" class="bi bi-shield-check text-primary" title="Admin"></i>
            </td>
            <td class="text-end text-nowrap">
              <template v-if="user.isClientAdmin !== 'true'">
                <button v-if="user.isApiKey !== 'true' && !isAdminMode" class="btn btn-sm btn-link text-primary p-0 me-2" @click="promptChangePassword(user)" title="Change password" :disabled="isBusy">
                  <i class="bi bi-key"></i>
                </button>
                <button class="btn btn-sm btn-link text-warning p-0 me-2" @click="promptResetApiKey(user)" title="Reset API key" :disabled="isBusy">
                  <i class="bi bi-arrow-repeat"></i>
                </button>
                <button class="btn btn-sm btn-link text-danger p-0" @click="promptRemoveUser(user)" title="Remove user" :disabled="isBusy">
                  <i class="bi bi-trash3"></i>
                </button>
              </template>
              <span v-else class="text-muted small">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Confirmation / input modal -->
    <div class="modal" :class="{ show: modal.isOpen }" :style="{ display: modal.isOpen ? 'block' : 'none' }" tabindex="-1" @click.self="closeModal">
      <div v-if="modal.isOpen" class="modal-backdrop fade show" @click="closeModal"></div>
      <div class="modal-dialog modal-dialog-centered" style="z-index:1060;">
        <div class="modal-content">
          <div class="modal-header py-2">
            <h6 class="modal-title">{{ modal.title }}</h6>
            <button type="button" class="btn-close btn-close-sm" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <p>{{ modal.message }}</p>
            <div v-if="modal.showInput">
              <label class="form-label form-label-sm">{{ modal.inputLabel }}</label>
              <input :type="modal.inputType || 'text'" class="form-control form-control-sm" v-model="modal.inputValue" @keyup.enter="confirmModal" />
            </div>
            <!-- Show result after API key reset -->
            <div v-if="modal.resultMessage" class="alert alert-success py-2 mt-2 mb-0 small" style="cursor:pointer;" @click="copyApiKey(modal.resultKey, 'modal')" title="Click to copy">
              {{ copiedKey === 'modal' ? 'Copied!' : modal.resultMessage }}
            </div>
          </div>
          <div class="modal-footer py-2">
            <button class="btn btn-sm btn-secondary" @click="closeModal">{{ modal.resultMessage ? 'Close' : 'Cancel' }}</button>
            <button v-if="!modal.resultMessage" class="btn btn-sm" :class="modal.confirmClass || 'btn-primary'" @click="confirmModal" :disabled="isBusy || (modal.showInput && !modal.inputValue)">
              <span v-if="isBusy" class="spinner-border spinner-border-sm me-1"></span>{{ modal.confirmLabel || 'Confirm' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_bulkdata";
import "/src/visualcrossing-core/vccommunicator_serveradmin";

export default {
  name: 'ChildUserManager',
  props: {
    maxChildUsers: { type: Number, default: 0 },
    targetUser: { type: String, default: null },
  },
  data() {
    return {
      childUsers: [],
      isLoading: false,
      isBusy: false,
      errorMsg: null,
      showAddForm: false,
      addMode: 'apikey',
      newEmail: '',
      newPassword: '',
      newLabel: '',
      copiedKey: null,
      modal: {
        isOpen: false,
        title: '',
        message: '',
        showInput: false,
        inputLabel: '',
        inputType: 'text',
        inputValue: '',
        confirmLabel: '',
        confirmClass: '',
        resultMessage: null,
        resultKey: null,
        action: null,
        user: null,
      },
    };
  },
  computed: {
    effectiveMaxUsers() {
      return this.maxChildUsers > 0 ? this.maxChildUsers : 1;
    },
    canAddUser() {
      return this.childUsers.length < this.effectiveMaxUsers;
    },
    isAdminMode() {
      return !!this.targetUser;
    },
  },
  methods: {
    loadChildUsers() {
      var me = this;
      me.isLoading = true;
      me.errorMsg = null;
      var promise = me.targetUser
        ? window.vcCommunicator.getUserChildUsers(me.targetUser)
        : window.vcCommunicator.listChildUsers();
      promise
        .then(function(users) {
          me.childUsers = users || [];
        })
        .catch(function(err) {
          me.errorMsg = err || 'Failed to load team members';
        })
        .finally(function() {
          me.isLoading = false;
        });
    },

    displayName(user) {
      if (user.fullName) return user.fullName;
      if (user.email) return user.email;
      return user.userName;
    },

    copyApiKey(key, id) {
      var me = this;
      if (navigator.clipboard && key) {
        navigator.clipboard.writeText(key).then(function() {
          me.copiedKey = id;
          setTimeout(function() { me.copiedKey = null; }, 1500);
        });
      }
    },

    createUser() {
      var me = this;
      var isApiKey = me.addMode === 'apikey';
      if (isApiKey) {
        if (!me.newLabel) return;
        if (me.newLabel.length > 200) {
          me.errorMsg = 'Label must be 200 characters or fewer';
          return;
        }
      } else {
        if (!me.newEmail) return;
        if (me.newEmail.length < 3) {
          me.errorMsg = 'Email must be at least 3 characters';
          return;
        }
        if (me.newEmail.length > 200) {
          me.errorMsg = 'Email must be 200 characters or fewer';
          return;
        }
        if (!/^[a-zA-Z0-9@._\-]+$/.test(me.newEmail)) {
          me.errorMsg = 'Email can only contain letters, numbers, @, dots, hyphens, or underscores';
          return;
        }
      }
      me.isBusy = true;
      me.errorMsg = null;
      var createPromise = me.targetUser
        ? window.vcCommunicator.adminCreateChildUser(
            me.targetUser,
            isApiKey ? null : me.newEmail,
            isApiKey ? null : me.newPassword,
            me.addMode,
            isApiKey ? me.newLabel : null
          )
        : window.vcCommunicator.createChildUser(
            isApiKey ? null : me.newEmail,
            isApiKey ? null : me.newPassword,
            me.addMode,
            isApiKey ? me.newLabel : null
          );
      createPromise
        .then(function() {
          me.cancelAdd();
          me.loadChildUsers();
        })
        .catch(function(err) {
          me.errorMsg = err || 'Failed to create user';
        })
        .finally(function() {
          me.isBusy = false;
        });
    },

    cancelAdd() {
      this.showAddForm = false;
      this.newEmail = '';
      this.newPassword = '';
      this.newLabel = '';
    },

    promptRemoveUser(user) {
      var name = this.displayName(user);
      this.openModal({
        title: 'Remove ' + (user.isApiKey === 'true' ? 'API Key' : 'User'),
        message: 'Remove ' + name + ' from your team? This cannot be undone.',
        confirmLabel: 'Remove',
        confirmClass: 'btn-danger',
        action: 'remove',
        user: user,
      });
    },

    promptChangePassword(user) {
      var name = this.displayName(user);
      this.openModal({
        title: 'Change Password',
        message: 'Set a new password for ' + name + ':',
        showInput: true,
        inputLabel: 'New password',
        inputType: 'text',
        confirmLabel: 'Change Password',
        action: 'changePassword',
        user: user,
      });
    },

    promptResetApiKey(user) {
      var name = this.displayName(user);
      this.openModal({
        title: 'Reset API Key',
        message: 'Generate a new API key for ' + name + '? The current key will stop working immediately.',
        confirmLabel: 'Reset Key',
        confirmClass: 'btn-warning',
        action: 'resetApiKey',
        user: user,
      });
    },

    openModal(opts) {
      this.modal = {
        isOpen: true,
        title: opts.title || '',
        message: opts.message || '',
        showInput: opts.showInput || false,
        inputLabel: opts.inputLabel || '',
        inputType: opts.inputType || 'text',
        inputValue: '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        confirmClass: opts.confirmClass || 'btn-primary',
        resultMessage: null,
        resultKey: null,
        action: opts.action,
        user: opts.user,
      };
    },

    closeModal() {
      this.modal.isOpen = false;
    },

    confirmModal() {
      var me = this;
      var action = me.modal.action;
      var user = me.modal.user;
      me.isBusy = true;
      me.errorMsg = null;

      var promise;
      if (action === 'remove') {
        promise = (me.targetUser
          ? window.vcCommunicator.adminRemoveChildUser(me.targetUser, user.userName)
          : window.vcCommunicator.removeChildUser(user.userName))
          .then(function() {
            me.closeModal();
            me.loadChildUsers();
          });
      } else if (action === 'changePassword') {
        promise = window.vcCommunicator.changeChildPassword(user.userName, me.modal.inputValue)
          .then(function() {
            me.closeModal();
          });
      } else if (action === 'resetApiKey') {
        promise = (me.targetUser
          ? window.vcCommunicator.adminResetChildApiKey(me.targetUser, user.userName)
          : window.vcCommunicator.resetChildApiKey(user.userName))
          .then(function(result) {
            var newKey = result.newKey || '';
            me.modal.resultMessage = 'New API key: ' + (newKey || '(see user list)');
            me.modal.resultKey = newKey;
            me.loadChildUsers();
          });
      }

      if (promise) {
        promise
          .catch(function(err) {
            me.errorMsg = err || 'Operation failed';
            me.closeModal();
          })
          .finally(function() {
            me.isBusy = false;
          });
      }
    },
  },

  mounted() {
    this.loadChildUsers();
  },
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}
.modal.show {
  display: block !important;
}
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: #000;
}
.modal-backdrop.show {
  opacity: 0.5;
}
</style>
